import {
  faCancel,
  faDownload,
  faEdit,
  faEye,
  faFileInvoice,
  faFilePdf,
  faPlus,
  faSearch,
  faShieldHalved,
  faSpinner,
  faStamp,
  faTrash,
  faTurnDown,
  faUpDown,
  faUpload,
  faUpLong,
  faRotateLeft,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import 'chart.js/auto'
import { subDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Table,
  Tooltip,
} from 'react-bootstrap'
import { Doughnut } from 'react-chartjs-2'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Navbar from '../../components/navbar/navbar.jsx'
import apinode from '../../services/apinode.js'
import apidfe from '../../services/apidfe.js'

const POR_PAGINA = 50

// ─── Helper CFOP ─────────────────────────────────────────────────────────────
function cfopDevolucao(cfop) {
  const map = {
    // Vendas dentro do estado → devolução entrada interna
    5101: '1201',
    5102: '1202',
    5103: '1205',
    5405: '1411',
    // Vendas fora do estado → devolução entrada interestadual
    6101: '2201',
    6102: '2202',
    6108: '2202',
    6404: '2202',
    6103: '2205',
    6405: '2411',
    // Compras → devolução saída para fornecedor
    1102: '5202',
    2102: '6202',
  }

  const str = String(cfop || '').trim()
  const convertido = map[str]

  if (!convertido) {
    console.warn(`⚠️ CFOP sem mapeamento de devolução: ${str}`)
  }

  return convertido ?? str
}

const fmt = (v) =>
  parseFloat(v || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })

// ─── Modal de prévia ──────────────────────────────────────────────────────────
function ModalDevolucao({ nfe, onClose, onConfirm, loading }) {
  if (!nfe) return null

  const itens = nfe.itens || []
  const totalGeral = itens.reduce(
    (a, i) => a + parseFloat(i.VALORTOTAL || 0),
    0,
  )

  return (
    <Modal
      show
      onHide={onClose}
      size="xl"
      centered
      scrollable
    >
      <Modal.Header
        closeButton
        style={{
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: '#fff',
          borderBottom: 'none',
        }}
      >
        <Modal.Title style={{ fontWeight: 700, fontSize: '1rem' }}>
          <FontAwesomeIcon
            icon={faRotateLeft}
            className="me-2"
          />
          Prévia — Nota de Devolução · NF-e nº {nfe.numnfe}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '20px 24px', background: '#f8fafc' }}>
        {/* Aviso */}
        <div
          style={{
            background: '#fef3c7',
            border: '1.5px solid #fcd34d',
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 20,
            fontSize: '0.82rem',
            color: '#78350f',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            Será gerada uma <strong>NFe de entrada (devolução)</strong>{' '}
            espelhando a NF-e nº <strong>{nfe.numnfe}</strong>. Os CFOPs serão
            convertidos automaticamente:{' '}
            <strong>5xxx→1xxx · 6xxx→2xxx · 7xxx→3xxx</strong>. Revise os dados
            antes de confirmar.
          </div>
        </div>

        {/* Cabeçalho em cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12,
            marginBottom: 20,
          }}
        >
          {[
            {
              label: 'NF-e Referenciada',
              value: `Nº ${nfe.numnfe} · Série ${nfe.serie || 1}`,
            },
            {
              label: 'Destinatário',
              value: nfe.pes_razao
                ?.toLowerCase()
                .replace(/\b\w/g, (c) => c.toUpperCase()),
            },
            {
              label: 'Valor Total',
              value: fmt(nfe.totalpedido),
              highlight: true,
            },
            { label: 'Natureza', value: '🔄 Entrada — Devolução', badge: true },
            { label: 'Frete', value: fmt(nfe.valorfrete) },
            { label: 'Chave de Acesso', value: nfe.chave || '—', small: true },
          ].map(({ label, value, highlight, badge, small }) => (
            <div
              key={label}
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '12px 16px',
              }}
            >
              <div
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 4,
                }}
              >
                {label}
              </div>
              <div
                style={{
                  fontWeight: highlight ? 800 : 600,
                  color: highlight ? '#059669' : '#1e293b',
                  fontSize: highlight
                    ? '1.05rem'
                    : small
                      ? '0.7rem'
                      : '0.88rem',
                  wordBreak: 'break-all',
                }}
              >
                {badge ? (
                  <Badge
                    bg="warning"
                    text="dark"
                  >
                    {value}
                  </Badge>
                ) : (
                  value
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Tabela de itens */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #e2e8f0',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            📦 Itens da Nota
            <span
              style={{
                background: '#eef2ff',
                color: '#4f46e5',
                fontSize: '0.7rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 99,
              }}
            >
              {itens.length} item(s)
            </span>
          </div>

          {itens.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.8rem',
                }}
              >
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    {[
                      '#',
                      'SKU',
                      'Descrição',
                      'Qtde',
                      'Vl. Unit.',
                      'Desconto',
                      'Total',
                      'CFOP Orig.',
                      'CFOP Dev.',
                      'NCM',
                      'CST',
                    ].map((h, i) => (
                      <th
                        key={h}
                        style={{
                          padding: '10px 12px',
                          textAlign: i >= 3 && i <= 6 ? 'right' : 'left',
                          fontWeight: 700,
                          color: '#64748b',
                          fontSize: '0.68rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          borderBottom: '1px solid #e2e8f0',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {itens.map((item, i) => (
                    <tr
                      key={i}
                      style={{
                        borderBottom: '1px solid #f0f4f8',
                        background: i % 2 === 0 ? '#fff' : '#fafbfc',
                      }}
                    >
                      <td
                        style={{
                          padding: '10px 12px',
                          color: '#94a3b8',
                          fontWeight: 600,
                        }}
                      >
                        {i + 1}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          fontWeight: 700,
                          color: '#4f46e5',
                        }}
                      >
                        {item.sku || item.SKU || item.IDPRODUTO || '—'}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          color: '#374151',
                          maxWidth: 200,
                        }}
                      >
                        <span
                          style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.descricao || item.DESCRICAO || '—'}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          textAlign: 'right',
                          fontWeight: 600,
                        }}
                      >
                        {parseFloat(item.QTDE || item.qtde || 0).toLocaleString(
                          'pt-BR',
                          { minimumFractionDigits: 2 },
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        {fmt(item.VALORUNITARIO || item.valorunitario)}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          textAlign: 'right',
                          color: '#dc2626',
                        }}
                      >
                        {parseFloat(item.DESCONTO || item.desconto || 0) > 0 ? (
                          fmt(item.DESCONTO || item.desconto)
                        ) : (
                          <span style={{ color: '#cbd5e1' }}>—</span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          textAlign: 'right',
                          fontWeight: 700,
                          color: '#059669',
                        }}
                      >
                        {fmt(item.VALORTOTAL || item.valortotal)}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span
                          style={{
                            color: '#94a3b8',
                            textDecoration: 'line-through',
                            fontSize: '0.75rem',
                          }}
                        >
                          {item.CFOP || item.cfop}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span
                          style={{
                            background: '#fef3c7',
                            color: '#92400e',
                            padding: '2px 8px',
                            borderRadius: 6,
                            fontWeight: 700,
                            fontSize: '0.75rem',
                          }}
                        >
                          {cfopDevolucao(item.CFOP || item.cfop)}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          color: '#64748b',
                          fontSize: '0.75rem',
                        }}
                      >
                        {item.NCM || item.ncm || '—'}
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          color: '#64748b',
                          fontSize: '0.75rem',
                        }}
                      >
                        {item.CSTICMS || item.csticms || item.CSOSN || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr
                    style={{
                      background: '#f0fdf4',
                      borderTop: '2px solid #bbf7d0',
                    }}
                  >
                    <td
                      colSpan={6}
                      style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontWeight: 700,
                        color: '#374151',
                      }}
                    >
                      Total Geral:
                    </td>
                    <td
                      style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontWeight: 800,
                        color: '#059669',
                        fontSize: '0.95rem',
                      }}
                    >
                      {fmt(totalGeral)}
                    </td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
              Nenhum item encontrado.
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer
        style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}
      >
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="warning"
          onClick={onConfirm}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 600,
          }}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm" /> Gerando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faRotateLeft} /> Confirmar e Gerar
              Devolução
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// ─── Paginação ────────────────────────────────────────────────────────────────
function Paginacao({ total, pagina, setPagina }) {
  const totalPaginas = Math.ceil(total / POR_PAGINA)
  if (totalPaginas <= 1) return null

  const inicio = (pagina - 1) * POR_PAGINA + 1
  const fim = Math.min(pagina * POR_PAGINA, total)

  // Gera páginas visíveis (max 7)
  const paginas = []
  const delta = 2
  for (
    let i = Math.max(1, pagina - delta);
    i <= Math.min(totalPaginas, pagina + delta);
    i++
  ) {
    paginas.push(i)
  }

  return (
    <div
      id="navigator-pages"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        background: '#fff',
        borderTop: '1px solid #e2e8f0',
        borderRadius: '0 0 8px 8px',
      }}
    >
      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
        Exibindo{' '}
        <strong>
          {inicio}–{fim}
        </strong>{' '}
        de <strong>{total}</strong> NF-es
      </span>
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          onClick={() => setPagina((p) => Math.max(1, p - 1))}
          disabled={pagina === 1}
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid #e2e8f0',
            background: pagina === 1 ? '#f8fafc' : '#fff',
            cursor: pagina === 1 ? 'not-allowed' : 'pointer',
            color: pagina === 1 ? '#cbd5e1' : '#374151',
          }}
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            size="xs"
          />
        </button>

        {paginas[0] > 1 && (
          <>
            <button
              className={`button-page ${btnPageClassName(1 === pagina)}`}
              onClick={() => setPagina(1)}
              style={btnPage(1 === pagina)}
            >
              1
            </button>
            {paginas[0] > 2 && (
              <span style={{ padding: '6px 4px', color: '#94a3b8' }}>…</span>
            )}
          </>
        )}

        {paginas.map((p) => (
          <button
            key={p}
            className={`button-page ${btnPageClassName(p === pagina)}`}
            onClick={() => setPagina(p)}
            style={btnPage(p === pagina)}
          >
            {p}
          </button>
        ))}

        {paginas[paginas.length - 1] < totalPaginas && (
          <>
            {paginas[paginas.length - 1] < totalPaginas - 1 && (
              <span style={{ padding: '6px 4px', color: '#94a3b8' }}>…</span>
            )}
            <button
              onClick={() => setPagina(totalPaginas)}
              style={btnPage(totalPaginas === pagina)}
              className={`button-page ${btnPageClassName(totalPaginas === pagina)}`}
            >
              {totalPaginas}
            </button>
          </>
        )}

        <button
          onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
          disabled={pagina === totalPaginas}
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid #e2e8f0',
            background: pagina === totalPaginas ? '#f8fafc' : '#fff',
            cursor: pagina === totalPaginas ? 'not-allowed' : 'pointer',
            color: pagina === totalPaginas ? '#cbd5e1' : '#374151',
          }}
        >
          <FontAwesomeIcon
            icon={faChevronRight}
            size="xs"
          />
        </button>
      </div>
    </div>
  )
}

function btnPageClassName(ativo) {
  return ativo ? 'active' : ''.trim()
}

function btnPage(ativo) {
  return {
    padding: '6px 10px',
    borderRadius: 6,
    border: `1px solid ${ativo ? '#4f46e5' : '#e2e8f0'}`,
    background: ativo ? '#4f46e5' : '#fff',
    color: ativo ? '#fff' : '#374151',
    fontWeight: ativo ? 700 : 400,
    cursor: 'pointer',
    minWidth: 32,
    fontSize: '0.8rem',
  }
}

// ─── Componente principal ─────────────────────────────────────────────────────
const ListaNFe = () => {
  const [nfes, setNfes] = useState([])
  const [empresa, setEmpresas] = useState([])
  const [empresaSelecionada, setEmpresaSelecionada] = useState('')
  const [termoPesquisa, setTermoPesquisa] = useState('')
  const [tipoPesquisa, setTipoPesquisa] = useState('numeroNfe')
  const [isLoading, setLoading] = useState(false)
  const [dataInicial, setDataInicial] = useState(null)
  const [dataFinal, setDataFinal] = useState(null)
  const [pagina, setPagina] = useState(1)

  const [nfeDevolucao, setNfeDevolucao] = useState(null)
  const [loadingDevolucao, setLoadingDevolucao] = useState(false)
  const [loadingPrevia, setLoadingPrevia] = useState({})

  const [loadingStates, setLoadingStates] = useState({
    protocolo: {},
    consulta: {},
    cancelamento: {},
    dadosFiscais: {},
    delete: {},
    consultaSefaz: {},
    danfe: {},
    adicionarProtocolo: {},
  })

  const [stats, setStats] = useState({
    total: 0,
    transmitidas: 0,
    erros: 0,
    canceladas: 0,
    emDigitacao: 0,
  })
  const navigate = useNavigate()

  const setLoadingState = useCallback((type, id, value) => {
    setLoadingStates((prev) => ({
      ...prev,
      [type]: { ...prev[type], [id]: value },
    }))
  }, [])

  const updateLinkPrefix = (link) =>
    link?.replace(
      'http://erp.belahome.com.br:9091',
      'http://fiscal.ziphub.com.br',
    ) || ''

  const CircularProgressBar = ({ percentage, label }) => {
    const data = {
      datasets: [
        {
          data: [percentage, 100 - percentage],
          backgroundColor: [
            'rgba(220, 53, 69, 0.8)',
            'rgba(214, 214, 214, 0.2)',
          ],
          borderWidth: 0,
        },
      ],
    }
    return (
      <div className="text-center">
        <div
          style={{
            width: '80px',
            height: '80px',
            margin: '0 auto',
            position: 'relative',
          }}
        >
          <Doughnut
            data={data}
            options={{
              cutout: '70%',
              plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
              },
              maintainAspectRatio: true,
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#dc3545',
            }}
          >
            {percentage}%
          </div>
        </div>
        <small className="text-muted mt-2 d-block">{label}</small>
      </div>
    )
  }

  const calcularEstatisticas = useCallback((nfes) => {
    setStats({
      total: nfes.length,
      transmitidas: nfes.filter((n) => n.status === 'T').length,
      erros: nfes.filter((n) => n.status === 'R').length,
      canceladas: nfes.filter((n) => n.status === 'I').length,
      emDigitacao: nfes.filter((n) => n.status === 'D').length,
    })
  }, [])

  const handleAbrirDevolucao = useCallback(async (nfe) => {
    try {
      setLoadingPrevia((prev) => ({ ...prev, [nfe.id]: true }))
      const response = await apinode.get(
        `/nfe/lista/${nfe.id}/${localStorage.idconta}`,
      )
      setNfeDevolucao({ ...nfe, ...response.data[0] })
    } catch {
      toast.error('Erro ao carregar dados da nota para devolução.')
    } finally {
      setLoadingPrevia((prev) => ({ ...prev, [nfe.id]: false }))
    }
  }, [])

  const handleConfirmarDevolucao = useCallback(async () => {
    if (!nfeDevolucao) return
    try {
      setLoadingDevolucao(true)
      const response = await apinode.post(
        `/nfe/devolucao/${nfeDevolucao.id}/${localStorage.idconta}`,
      )
      toast.success(response.data.message)
      setNfeDevolucao(null)

      if (response.data.idnfe_devolucao) {
        window.open(
          `/nfe/editar/${response.data.idnfe_devolucao}`,
          '_blank',
          'noopener,noreferrer',
        )
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao gerar devolução.')
    } finally {
      setLoadingDevolucao(false)
    }
  }, [nfeDevolucao])

  const handleXml = useCallback(async (idnfe, chave) => {
    try {
      const response = await apinode({
        url: `/nfe/fiscal_xml/${localStorage.idconta}`,
        method: 'GET',
        params: { id: idnfe },
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(
        new Blob([await response.data.text()]),
      )
      const a = document.createElement('a')
      a.href = url
      a.setAttribute('download', `${chave}-nfe.xml`)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch {
      toast.error('Erro ao baixar o arquivo XML')
    }
  }, [])

  const handlePdf = useCallback(async (link, idnfe) => {
    try {
      if (!link || !link.includes('http://erp.belahome.com.br:9091')) {
        const response = await apinode({
          url: `/nfe/fiscal_pdf/${localStorage.idconta}`,
          method: 'GET',
          params: { id: idnfe },
        })
        const w = window.open('', '_blank')
        w.document.body.innerHTML = response.data
        w.print()
      } else {
        window.open(updateLinkPrefix(link), '_blank')
      }
    } catch {
      toast.error('Erro ao visualizar o PDF')
    }
  }, [])

  const handleProtocolo = useCallback(
    async (id, contaEmp) => {
      try {
        setLoadingState('protocolo', id, true)
        const r = await apinode.get(
          `/nfe/protocolo/${id}/${contaEmp}/${localStorage.idconta}`,
        )
        toast.success(r.data)
      } catch {
        toast.error('Erro ao consultar protocolo')
      } finally {
        setLoadingState('protocolo', id, false)
      }
    },
    [setLoadingState],
  )

  const handleConsultarStatus = useCallback(
    async (id, contaEmp) => {
      try {
        setLoadingState('consulta', id, true)
        const r = await apinode.get(
          `/nfe/consultar/${id}/${contaEmp}/${localStorage.idconta}`,
        )
        toast[r.data.status ? 'success' : 'warning'](r.data.message)
      } catch {
        toast.error('Erro ao consultar status')
      } finally {
        setLoadingState('consulta', id, false)
      }
    },
    [setLoadingState],
  )

  const handleCancelarNFE = useCallback(
    async (id) => {
      try {
        if (!window.confirm('Deseja realmente cancelar esta NF-e?')) return
        setLoadingState('cancelamento', id, true)
        const r = await apinode.get(
          `/nfe/cancelamento_nfe/${id}/${localStorage.idconta}`,
        )
        toast.success(r.data.retorno)
        fetchNfesInicial(empresaSelecionada)
      } catch {
        toast.error('Erro ao cancelar NF-e')
      } finally {
        setLoadingState('cancelamento', id, false)
      }
    },
    [empresaSelecionada, setLoadingState],
  )

  const handleEditarCep = useCallback(
    async (id) => {
      try {
        const cep = window.prompt('Digite o novo CEP:')
        if (!cep) return
        const cepLimpo = cep.replace(/\D/g, '')
        if (cepLimpo.length !== 8) {
          toast.error('CEP inválido.')
          return
        }
        if (!window.confirm(`Confirma atualizar o CEP para ${cepLimpo}?`))
          return
        setLoadingState('cancelamento', id, true)
        await apinode.post(`/ajuste-cep-edita/${id}/${localStorage.idconta}`, {
          cepDestinatario: cepLimpo,
        })
        toast.success('CEP atualizado!')
        fetchNfesInicial(empresaSelecionada)
      } catch {
        toast.error('Erro ao atualizar o CEP')
      } finally {
        setLoadingState('cancelamento', id, false)
      }
    },
    [empresaSelecionada, setLoadingState],
  )

  const handleDelete = useCallback(
    async (id) => {
      try {
        if (!window.confirm('Tem certeza de que deseja excluir esta NF-e?'))
          return
        setLoadingState('delete', id, true)
        const r = await apinode.delete(`/nfe/${id}/${localStorage.idconta}`)
        toast.success(r.data.message)
        fetchNfesInicial(empresaSelecionada)
      } catch {
        toast.error('Erro ao excluir a NF-e')
      } finally {
        setLoadingState('delete', id, false)
      }
    },
    [empresaSelecionada, setLoadingState],
  )

  const handleDadosFicais = useCallback(
    async (id, contaEmp) => {
      try {
        setLoadingState('dadosFiscais', id, true)
        await apinode.get(
          `/nfe/dadosfiscais/${id}/${contaEmp}/${localStorage.idconta}`,
        )
        toast.success('Dados fiscais enviados!')
      } catch {
        toast.error('Erro ao enviar os dados fiscais!')
      } finally {
        setLoadingState('dadosFiscais', id, false)
      }
    },
    [setLoadingState],
  )

  const handleConsultarSefaz = useCallback(
    async (id, conta) => {
      try {
        setLoadingState('consultaSefaz', id, true)
        const { data } = await apidfe.get(`/gnre/consultar-nfe/${conta}/${id}`)
        const { cStat, xMotivo, protocolo } = data
        if (cStat === '100')
          toast.success(
            `✅ ${cStat} — ${xMotivo}${protocolo ? ` | nProt: ${protocolo.nProt}` : ''}`,
          )
        else if (cStat === '101') toast.warning(`⚠️ ${cStat} — ${xMotivo}`)
        else toast.error(`❌ ${cStat} — ${xMotivo}`)
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Erro ao consultar na SEFAZ')
      } finally {
        setLoadingState('consultaSefaz', id, false)
      }
    },
    [setLoadingState],
  )

  const handleDanfe = useCallback(
    async (id, conta) => {
      try {
        setLoadingState('danfe', id, true)
        window.open(
          `${apidfe.defaults.baseURL}/gnre/danfe/${conta}/${id}`,
          '_blank',
        )
      } catch {
        toast.error('Erro ao abrir DANFE')
      } finally {
        setLoadingState('danfe', id, false)
      }
    },
    [setLoadingState],
  )

  const handleAdicionarProtocolo = useCallback(
    async (id, conta) => {
      try {
        setLoadingState('adicionarProtocolo', id, true)
        const { data } = await apidfe.post(`/gnre/protocolar-nfe/${conta}`, {
          nfe_id: id,
        })
        if (data.success) {
          toast.success(
            `✅ Protocolo: ${data.protocolo} — ${data.statusEmissao}`,
          )
          setNfes((prev) =>
            prev.map((n) =>
              n.id === id
                ? {
                    ...n,
                    protocolo: data.protocolo,
                    status_emissao: data.statusEmissao,
                  }
                : n,
            ),
          )
        } else {
          toast.warning(`⚠️ ${data.statusEmissao}: ${data.xMotivo}`)
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Erro ao adicionar protocolo')
      } finally {
        setLoadingState('adicionarProtocolo', id, false)
      }
    },
    [setLoadingState],
  )

  const fetchEmpresa = useCallback(async () => {
    try {
      setLoading(true)
      const r = await apinode.get(`/nfe/lista-empresa/${localStorage.idconta}`)
      setEmpresas(r.data)
      if (r.data.length > 0) {
        setEmpresaSelecionada(r.data[0].id_conta)
        await fetchNfesInicial(r.data[0].id_conta)
      }
    } catch {
      toast.error('Erro ao carregar empresas')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchNfesInicial = useCallback(
    async (idConta) => {
      try {
        setLoading(true)
        setPagina(1)
        const hoje = new Date()
        const r = await apinode.get(
          `/nfe/lista-nfe/${idConta}/${localStorage.idconta}`,
          { params: { dataIn: subDays(hoje, 5), dataFn: hoje } },
        )
        setNfes(r.data)
        calcularEstatisticas(r.data)
      } catch {
        toast.error('Erro ao carregar NF-es')
      } finally {
        setLoading(false)
      }
    },
    [calcularEstatisticas],
  )

  const fetchNfes = useCallback(
    async (idConta) => {
      try {
        if (!dataInicial || !dataFinal) {
          toast.warning('Selecione as datas')
          return
        }
        if (
          dataInicial.toISOString().split('T')[0] ===
          dataFinal.toISOString().split('T')[0]
        ) {
          toast.warning('Datas iguais')
          return
        }
        setLoading(true)
        setPagina(1)
        const r = await apinode.get(
          `/nfe/lista-nfe/${idConta}/${localStorage.idconta}`,
          { params: { dataIn: dataInicial, dataFn: dataFinal } },
        )
        setNfes(r.data)
        calcularEstatisticas(r.data)
      } catch {
        toast.error('Erro ao pesquisar NF-es')
      } finally {
        setLoading(false)
      }
    },
    [dataInicial, dataFinal, calcularEstatisticas],
  )

  const nfesFiltradas = useMemo(() => {
    if (!termoPesquisa) return nfes
    return nfes.filter((nfe) => {
      const t = termoPesquisa.toLowerCase()
      switch (tipoPesquisa) {
        case 'numeroNfe':
          return (nfe.numnfe || '').toString().includes(termoPesquisa)
        case 'destinatario':
          return nfe.pes_razao.toLowerCase().includes(t)
        case 'id':
          return nfe.id.toString().includes(termoPesquisa)
        case 'numvenda':
          return (nfe.numero_venda || '').toString().includes(termoPesquisa)
        default:
          return true
      }
    })
  }, [nfes, termoPesquisa, tipoPesquisa])

  // Reset paginação ao filtrar
  useEffect(() => {
    setPagina(1)
  }, [termoPesquisa, tipoPesquisa])

  const nfesPagina = useMemo(() => {
    const inicio = (pagina - 1) * POR_PAGINA
    return nfesFiltradas.slice(inicio, inicio + POR_PAGINA)
  }, [nfesFiltradas, pagina])

  useEffect(() => {
    const hoje = new Date()
    setDataInicial(subDays(hoje, 5))
    setDataFinal(hoje)
    fetchEmpresa()
  }, [fetchEmpresa])

  const getStatusBadge = (status) => {
    const map = {
      T: ['success', 'Transmitida'],
      R: ['danger', 'Erro'],
      D: ['info', 'Em Digitação'],
      I: ['warning', 'Cancelada'],
    }
    const [bg, text] = map[status] || ['secondary', 'Desconhecido']
    return <Badge bg={bg}>{text}</Badge>
  }

  const formatarNomeUsuario = (nome) =>
    nome
      ? nome
          .replace(/[^a-zA-ZÀ-ÿ\s]/g, '')
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .join(' ')
      : ''

  const Btn = ({ tip, variant, icon, loadKey, id, onClick, disabled }) => (
    <OverlayTrigger overlay={<Tooltip>{tip}</Tooltip>}>
      <Button
        variant={variant}
        size="sm"
        onClick={onClick}
        disabled={disabled || (loadKey && loadingStates[loadKey]?.[id])}
      >
        <FontAwesomeIcon
          icon={loadKey && loadingStates[loadKey]?.[id] ? faSpinner : icon}
          spin={!!(loadKey && loadingStates[loadKey]?.[id])}
        />
      </Button>
    </OverlayTrigger>
  )

  return (
    <>
      <Navbar />
      <div className="container-fluid mt-page">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">
              <FontAwesomeIcon
                icon={faFileInvoice}
                className="me-2"
              />
              Lista de NF-e
            </h2>
            <p
              className="text-muted mb-0"
              style={{ fontSize: '0.875rem' }}
            >
              Gerencie e consulte suas notas fiscais eletrônicas
            </p>
          </div>
          <Button
            variant="success"
            as={Link}
            to={`/nfe/editar/novo/${empresaSelecionada}`}
            disabled={!empresaSelecionada}
          >
            <FontAwesomeIcon
              icon={faPlus}
              className="me-2"
            />
            Nova NF-e
          </Button>
        </div>

        {/* Stats */}
        <div className="row mb-4">
          {[
            { label: 'Total', value: stats.total, color: '' },
            {
              label: 'Transmitidas',
              value: stats.transmitidas,
              color: 'success',
            },
            { label: 'Com Erro', value: stats.erros, color: 'danger' },
            { label: 'Canceladas', value: stats.canceladas, color: 'warning' },
            { label: 'Em Digitação', value: stats.emDigitacao, color: 'info' },
          ].map(({ label, value, color }) => (
            <div
              className="col-md-2"
              key={label}
            >
              <Card className={`text-center${color ? ` border-${color}` : ''}`}>
                <Card.Body className="py-3">
                  <h3 className={`mb-0${color ? ` text-${color}` : ''}`}>
                    {value}
                  </h3>
                  <small className="text-muted">{label}</small>
                </Card.Body>
              </Card>
            </div>
          ))}
          <div className="col-md-2">
            <Card>
              <Card.Body className="p-2">
                <CircularProgressBar
                  percentage={
                    stats.total > 0
                      ? Math.round((stats.erros / stats.total) * 100)
                      : 0
                  }
                  label="Taxa de Erro"
                />
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Filtros */}
        <Card className="mb-4">
          <Card.Body>
            <div className="row g-3">
              <div className="col-md-3">
                <Form.Label>Empresa</Form.Label>
                <Form.Select
                  value={empresaSelecionada}
                  onChange={(e) => {
                    setEmpresaSelecionada(e.target.value)
                    fetchNfesInicial(e.target.value)
                  }}
                >
                  {empresa.map((emp) => (
                    <option
                      key={emp.id_conta}
                      value={emp.id_conta}
                    >
                      {emp.emp_razao} - {emp.emp_cnpj}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-2">
                <Form.Label>Data Inicial</Form.Label>
                <DatePicker
                  selected={dataInicial}
                  onChange={setDataInicial}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  locale={ptBR}
                />
              </div>
              <div className="col-md-2">
                <Form.Label>Data Final</Form.Label>
                <DatePicker
                  selected={dataFinal}
                  onChange={setDataFinal}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  locale={ptBR}
                />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <Button
                  variant="primary"
                  onClick={() => fetchNfes(empresaSelecionada)}
                  disabled={isLoading}
                  className="w-100"
                >
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="me-2"
                  />
                  Buscar
                </Button>
              </div>
              <div className="col-md-1">
                <Form.Label>Tipo</Form.Label>
                <Form.Select
                  value={tipoPesquisa}
                  onChange={(e) => setTipoPesquisa(e.target.value)}
                >
                  <option value="numeroNfe">Nº NF-e</option>
                  <option value="numvenda">Pedido</option>
                  <option value="destinatario">Cliente</option>
                  <option value="id">ID</option>
                </Form.Select>
              </div>
              <div className="col-md-2">
                <Form.Label>Pesquisar</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Pesquisar..."
                    value={termoPesquisa}
                    onChange={(e) => setTermoPesquisa(e.target.value)}
                  />
                  {termoPesquisa && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setTermoPesquisa('')}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Tabela */}
        <Card>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table
                id="table-nfe"
                className="table table-hover mb-0"
                style={{ fontSize: '0.82rem' }}
              >
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 420 }}>Ações</th>
                    <th>ID</th>
                    <th>Número</th>
                    <th>Série</th>
                    <th>Pedido</th>
                    <th>Emissão</th>
                    <th>Valor</th>
                    <th>Destinatário</th>
                    <th>Status</th>
                    <th>Usuário</th>
                    <th>Saída</th>
                    <th>Arquivos</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan="12"
                        className="text-center py-5"
                      >
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          size="2x"
                        />
                        <p className="mt-2 mb-0">Carregando...</p>
                      </td>
                    </tr>
                  ) : nfesPagina.length === 0 ? (
                    <tr>
                      <td
                        colSpan="12"
                        className="text-center py-5 text-muted"
                      >
                        Nenhuma NF-e encontrada
                      </td>
                    </tr>
                  ) : (
                    nfesPagina.map((nfe) => (
                      <tr key={nfe.id}>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            <Btn
                              tip="Editar"
                              variant="primary"
                              icon={faEdit}
                              onClick={() => navigate(`/nfe/editar/${nfe.id}`)}
                            />
                            <Btn
                              tip="Dados fiscais"
                              variant="info"
                              icon={faUpload}
                              loadKey="dadosFiscais"
                              id={nfe.id}
                              onClick={() =>
                                handleDadosFicais(nfe.id, nfe.emp_conta)
                              }
                            />
                            <Btn
                              tip="Protocolo"
                              variant="secondary"
                              icon={faTurnDown}
                              loadKey="protocolo"
                              id={nfe.id}
                              onClick={() =>
                                handleProtocolo(nfe.id, nfe.emp_conta)
                              }
                            />
                            <Btn
                              tip="Consultar Status"
                              variant="secondary"
                              icon={faUpDown}
                              loadKey="consulta"
                              id={nfe.id}
                              onClick={() =>
                                handleConsultarStatus(nfe.id, nfe.emp_conta)
                              }
                            />
                            <Btn
                              tip="Cancelar"
                              variant="warning"
                              icon={faCancel}
                              loadKey="cancelamento"
                              id={nfe.id}
                              onClick={() =>
                                handleCancelarNFE(nfe.id, nfe.emp_conta)
                              }
                            />
                            <Btn
                              tip="Editar CEP"
                              variant="warning"
                              icon={faUpLong}
                              id={nfe.id}
                              onClick={() => handleEditarCep(nfe.id)}
                            />
                            <Btn
                              tip="Excluir"
                              variant="danger"
                              icon={faTrash}
                              loadKey="delete"
                              id={nfe.id}
                              onClick={() => handleDelete(nfe.id)}
                            />
                            <Btn
                              tip="Consultar SEFAZ"
                              variant="dark"
                              icon={faShieldHalved}
                              loadKey="consultaSefaz"
                              id={nfe.id}
                              onClick={() =>
                                handleConsultarSefaz(
                                  nfe.id,
                                  localStorage.idconta,
                                )
                              }
                            />
                            <Btn
                              tip="DANFE PDF"
                              variant="danger"
                              icon={faFilePdf}
                              loadKey="danfe"
                              id={nfe.id}
                              onClick={() =>
                                handleDanfe(nfe.id, localStorage.idconta)
                              }
                            />
                            <Btn
                              tip="Adicionar Protocolo"
                              variant="success"
                              icon={faStamp}
                              loadKey="adicionarProtocolo"
                              id={nfe.id}
                              onClick={() =>
                                handleAdicionarProtocolo(
                                  nfe.id,
                                  localStorage.idconta,
                                )
                              }
                            />
                            {/* Devolução */}
                            <OverlayTrigger
                              overlay={<Tooltip>Nota de Devolução</Tooltip>}
                            >
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => handleAbrirDevolucao(nfe)}
                                disabled={loadingPrevia[nfe.id]}
                              >
                                <FontAwesomeIcon
                                  icon={
                                    loadingPrevia[nfe.id]
                                      ? faSpinner
                                      : faRotateLeft
                                  }
                                  spin={!!loadingPrevia[nfe.id]}
                                />
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                        <td style={{ color: '#64748b' }}>{nfe.id}</td>
                        <td>
                          <strong className="numero-nota">{nfe.numnfe}</strong>
                        </td>
                        <td>{nfe.serie}</td>
                        <td>
                          <a
                            href={`/pedido/editar/${nfe.numero_venda}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {nfe.numero_venda}
                          </a>
                        </td>
                        <td>
                          {new Date(nfe.datavenda).toLocaleDateString('pt-BR')}
                        </td>
                        <td>
                          <strong style={{ color: '#059669' }}>
                            {fmt(nfe.totalpedido)}
                          </strong>
                        </td>
                        <td style={{ maxWidth: 180 }}>
                          <span
                            style={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {nfe.pes_razao
                              .toLowerCase()
                              .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                        </td>
                        <td>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>{nfe.erro || 'Sem erros'}</Tooltip>
                            }
                          >
                            <span>{getStatusBadge(nfe.status)}</span>
                          </OverlayTrigger>
                        </td>
                        <td>
                          <small>
                            {formatarNomeUsuario(nfe.usuario_geracao)}
                          </small>
                        </td>
                        <td>
                          <small style={{ color: '#64748b' }}>
                            {new Date(nfe.datasaida).toLocaleDateString(
                              'pt-BR',
                            )}
                            <br />
                            {new Date(nfe.datasaida).toLocaleTimeString(
                              'pt-BR',
                            )}
                          </small>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Btn
                              tip="Ver PDF"
                              variant="outline-primary"
                              icon={faEye}
                              onClick={() => handlePdf(nfe.link_nfe, nfe.id)}
                            />
                            <Btn
                              tip="Baixar XML"
                              variant="outline-success"
                              icon={faDownload}
                              onClick={() => handleXml(nfe.id, nfe.chave)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Paginacao
              total={nfesFiltradas.length}
              pagina={pagina}
              setPagina={setPagina}
            />
          </Card.Body>
        </Card>
      </div>

      {nfeDevolucao && (
        <ModalDevolucao
          nfe={nfeDevolucao}
          onClose={() => setNfeDevolucao(null)}
          onConfirm={handleConfirmarDevolucao}
          loading={loadingDevolucao}
        />
      )}

      <style jsx="true">{`
        .table th {
          font-weight: 600;
          font-size: 0.8rem;
          white-space: nowrap;
        }
        .table td {
          vertical-align: middle;
        }
        .table-hover tbody tr:hover {
          background-color: rgba(79, 70, 229, 0.03) !important;
        }
        .btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
        }
        .card {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
          border-radius: 0.5rem;
        }
        .badge {
          font-weight: 500;
        }
        .form-label {
          font-weight: 500;
          font-size: 0.875rem;
        }
        .mt-page {
          margin-top: 20px;
          padding: 0 15px;
        }
      `}</style>
    </>
  )
}

export default ListaNFe
